import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, InlineNote, TopBar, useNote } from '../components/ui';
import { Icon } from '../components/Icon';
import { Screen } from '../components/Screen';
import { useApp } from '../lib/app';
import type { CountryCode } from 'libphonenumber-js/mobile';
import { phoneDisplay } from '../lib/format';
import { countryOf, e164, formatNational, maxDigits, startCountry } from '../lib/phone';
import { CountrySheet } from './Phone';
import type { Contact } from '../lib/types';
import { friendly } from '../lib/errors';
import { useT } from '../lib/i18n';


/** Trusted contacts (at most 5). Nothing is sent to them automatically yet — the rider shares the live link. */
export function Contacts() {
  const { api } = useApp();
  const [note, say] = useNote();
  const t = useT();
  const nav = useNavigate();
  const [list, setList] = useState<Contact[]>([]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState<CountryCode>(() => startCountry());
  const [picking, setPicking] = useState(false);
  const c = countryOf(country);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.contacts().then(setList, () => undefined);
  }, [api]);

  const add = async () => {
    // any country (the server takes E.164)
    const p = e164(phone, country);
    if (!name.trim() || !p) return say(t('contacts.need'), 'error');
    setBusy(true);
    try {
      const c = await api.addContact(name.trim(), p);
      setList((l) => [...l, c]);
      setName('');
      setPhone('');
    } catch (e) {
      say(friendly(e, t), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Screen name="account" label={t('contacts.title')}>
      <div className="zr-scroll" style={{ padding: 'calc(var(--sat) + 10px) 20px calc(var(--sab) + 16px)' }}>
        <TopBar onBack={() => nav({ to: '/account' })} title={t('contacts.title')} style={{ padding: 0 }} />
        <p style={{ margin: '20px 0 0', fontSize: 15, lineHeight: '22px', fontWeight: 500, color: 'var(--ink2)' }}>
          {t('contacts.body')}
        </p>
        <div className="zr-group zr-stagger" style={{ marginTop: 18 }}>
          {list.length === 0 && <div style={{ padding: 16, fontSize: 14, fontWeight: 500, color: 'var(--ink2)' }}>{t('contacts.none')}</div>}
          {list.map((c, i) => (
            <div key={c.id} style={{ height: 60, padding: '0 8px 0 16px', boxShadow: i ? 'inset 0 1px 0 var(--line)' : undefined, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 700 }}>{c.name}</span>
                <span style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--ink2)' }}>{phoneDisplay(c.phone)}</span>
              </span>
              <button
                type="button"
                aria-label={t('common.removeX', { name: c.name })}
                onClick={async () => {
                  await api.removeContact(c.id).catch(() => undefined);
                  setList((l) => l.filter((x) => x.id !== c.id));
                }}
                style={{ width: 44, height: 44, border: 0, background: 'transparent', color: 'var(--ink2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Icon name="trash" size={18} />
              </button>
            </div>
          ))}
        </div>
        {list.length < 5 && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void add();
            }}
            style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{t('contacts.add')}</h2>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('contacts.name')} aria-label={t('contacts.nameAria')} maxLength={60} className="zr-field" style={{ fontWeight: 700 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => setPicking(true)} aria-label={t('phone.countryAria', { name: c.name, dial: c.dial })} className="zr-chip" style={{ height: 56, borderRadius: 18 }}>
                <span aria-hidden="true" style={{ fontSize: 20 }}>{c.flag}</span>
                <span className="zr-num">{c.dial}</span>
                <Icon name="chevronDown" size={16} sw={2.2} />
              </button>
              <input value={formatNational(phone, country)} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, maxDigits(country)))} placeholder={t('contacts.phone')} aria-label={t('contacts.phoneAria')} inputMode="numeric" type="tel" className="zr-field" style={{ flex: 1, minWidth: 0, fontWeight: 700 }} />
            </div>
            <InlineNote note={note} style={{ margin: 0 }} />
            <Button type="submit" busy={busy} icon="plus">
              {t('contacts.addBtn')}
            </Button>
          </form>
        )}
      </div>
      <CountrySheet open={picking} onClose={() => setPicking(false)} value={country} only={null} onPick={(code) => (setCountry(code), setPicking(false))} />
    </Screen>
  );
}
